import { useEffect, useMemo, useState } from 'react';
import { PossibleCompetitorBidding } from 'services/actions/ai_management_possible_competitors';
import { geocodeCity } from 'services/google_maps';

export interface BiddingPlace {
  key: string;
  position: google.maps.LatLngLiteral;
  biddings: PossibleCompetitorBidding[];
}

type Positions = Record<string, google.maps.LatLngLiteral | null>;

const cityKey = (bidding: PossibleCompetitorBidding) => bidding.cidade?.trim() || '';

export function useBiddingPlaces(biddings: PossibleCompetitorBidding[], enabled: boolean) {
  const [positions, setPositions] = useState<Positions>({});
  const [resolving, setResolving] = useState(true);

  useEffect(() => {
    if (!enabled) return undefined;

    let active = true;
    const known: Positions = {};
    const missing: string[] = [];

    biddings.forEach((bidding) => {
      const key = cityKey(bidding);
      if (!key || key in known || missing.includes(key)) return;
      if (bidding.latitude && bidding.longitude) {
        known[key] = { lat: bidding.latitude, lng: bidding.longitude };
      } else {
        missing.push(key);
      }
    });

    setResolving(true);
    Promise.all(missing.map(geocodeCity)).then((found) => {
      if (!active) return;
      missing.forEach((city, index) => {
        known[city] = found[index];
      });
      setPositions(known);
      setResolving(false);
    });

    return () => {
      active = false;
    };
  }, [biddings, enabled]);

  const places = useMemo(() => {
    const byCity = new Map<string, BiddingPlace>();
    biddings.forEach((bidding) => {
      const key = cityKey(bidding);
      const position = positions[key];
      if (!position) return;
      const place = byCity.get(key) ?? { key, position, biddings: [] };
      place.biddings.push(bidding);
      byCity.set(key, place);
    });
    return Array.from(byCity.values());
  }, [biddings, positions]);

  const missingCount = useMemo(
    () => biddings.filter((bidding) => !positions[cityKey(bidding)]).length,
    [biddings, positions],
  );

  return { places, missingCount, resolving };
}
